// Driving screen: EU driving-limit exposure and driver fatigue profile.
// Port of the prototype's renderDriving.

import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { GROUPS, REGIONS, WORKERS, avgFED, sign, workersInRegion, wArr } from '../engine/data';
import type { Worker } from '../engine/data';
import { cdiTone, lrmTone } from '../engine/scores';
import { genInsight } from '../engine/insights';
import { AiInsight } from '../AiPanels';
import { scopedWorkers, useDash } from '../state';
import { AXIS_PROPS, GRID_PROPS, NO_ANIM, TOOLTIP_PROPS } from '../chartTheme';
import { Banner, Grid, Kpi, KpiRow, Panel, Pill, SecHeader, dataTable, fedTone, toneText } from '../ui';
import type { Tone } from '../ui';
import { cn } from '@/lib/utils';
import { GroupPicker, RegionPicker, StatTile, WorkerPicker } from './scopePickers';

const FED_BY_KM = [
  { bucket: '<200', v: 35 },
  { bucket: '200–400', v: 48 },
  { bucket: '400–600', v: 62 },
  { bucket: '600–800', v: 74 },
  { bucket: '>800', v: 88 },
];
const SLEEP_BY_DAYS = [
  { bucket: '0–1', v: 7.0 },
  { bucket: '2–3', v: 6.5 },
  { bucket: '4–5', v: 5.8 },
  { bucket: '6–7', v: 5.1 },
];

const DriveKpis: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const drivers = workers.filter((w) => w.driveKm > 0);
  const nonDrivers = workers.filter((w) => w.driveKm === 0);
  const euRisk = drivers.filter((w) => w.driveKm >= 700).length;
  const critLrm = drivers.filter((w) => w.lrm > 40).length;
  return (
    <KpiRow className='mb-4'>
      <Kpi label='Active Drivers' value={drivers.length} />
      <Kpi
        label='EU Limit Risk'
        value={euRisk}
        tone={euRisk ? 'bad' : 'good'}
        sub='Weekly km >700'
        subTrend={euRisk ? 'up' : undefined}
      />
      <Kpi
        label='Driver Critical LRM'
        value={critLrm}
        tone={critLrm ? 'bad' : 'good'}
        sub='High mismatch driving'
        subTrend={critLrm ? 'up' : undefined}
      />
      <Kpi label='Avg Driver FED' value={avgFED(drivers)} tone='warn' />
      <Kpi label='Avg Non-Driver FED' value={avgFED(nonDrivers)} tone='good' />
    </KpiRow>
  );
};

const DriveTable: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const drivers = workers.filter((w) => w.driveKm > 0);
  const { th, td, num, tr } = dataTable;
  return (
    <Panel
      className='mb-5'
      title='Driver Fatigue Profile'
      sub='LRM = readiness vs driving demand · driving km feeds demand side of mismatch'
    >
      {drivers.length ? (
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <thead>
              <tr>
                <th className={th}>Driver</th>
                {['Wk km', 'FED', 'CDI', 'LRM', 'Avg Sleep', 'Sleep Δ'].map((h) => (
                  <th key={h} className={cn(th, num)}>
                    {h}
                  </th>
                ))}
                <th className={th}>EU Limit</th>
                <th className={th}>WTR</th>
              </tr>
            </thead>
            <tbody>
              {[...drivers]
                .sort((a, b) => b.driveKm - a.driveKm)
                .map((w) => {
                  const avgSlp = w.slp7 ? +(w.slp7.reduce((a, v) => a + v, 0) / w.slp7.length).toFixed(1) : w.slp;
                  const slpDelta = +(avgSlp - w.slp_b).toFixed(1);
                  const euTone: Tone = w.driveKm >= 700 ? 'bad' : w.driveKm >= 500 ? 'warn' : 'good';
                  const slpTone: Tone = slpDelta <= -1.5 ? 'bad' : slpDelta <= -0.8 ? 'warn' : 'good';
                  return (
                    <tr key={w.init} onClick={() => drillWorker(w.init)} className={cn(tr, 'cursor-pointer')}>
                      <td className={td}>
                        <div className='font-semibold'>{w.name}</div>
                        <div className='text-[11px] text-muted-foreground'>{GROUPS[w.group].name}</div>
                      </td>
                      <td
                        className={cn(
                          td,
                          num,
                          'font-semibold',
                          euTone === 'good' ? 'text-muted-foreground' : toneText[euTone],
                        )}
                      >
                        {w.driveKm}
                      </td>
                      <td className={cn(td, num, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                      <td className={cn(td, num, 'font-semibold', toneText[cdiTone(w.cdi)])}>{w.cdi}</td>
                      <td className={cn(td, num, 'font-semibold', toneText[lrmTone(w.lrm)])}>
                        {w.lrm > 0 ? `+${w.lrm}` : w.lrm}
                      </td>
                      <td className={cn(td, num)}>{avgSlp}h</td>
                      <td className={cn(td, num, toneText[slpTone])}>
                        {sign(slpDelta)}
                        {slpDelta}h
                      </td>
                      <td className={td}>
                        <Pill tone={euTone}>
                          {euTone === 'bad' ? 'Critical' : euTone === 'warn' ? 'Monitor' : 'Within limit'}
                        </Pill>
                      </td>
                      <td className={td}>
                        <Pill tone={w.wtrFlag ? 'bad' : 'good'}>{w.wtrFlag ? '⚠ Breach' : 'OK'}</Pill>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      ) : (
        <Banner tone='info'>No active drivers in this scope.</Banner>
      )}
    </Panel>
  );
};

interface BucketChartProps {
  data: { bucket: string; v: number }[];
  domain: [number, number];
  fill: string;
  name: string;
}

const BucketChart: React.FC<BucketChartProps> = ({ data, domain, fill, name }) => (
  <div className='h-[200px]'>
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey='bucket' {...AXIS_PROPS} />
        <YAxis domain={domain} width={36} {...AXIS_PROPS} />
        <Tooltip {...TOOLTIP_PROPS} cursor={{ fill: 'var(--color-muted)', opacity: 0.4 }} />
        <Bar dataKey='v' name={name} fill={fill} radius={[3, 3, 0, 0]} {...NO_ANIM} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const DriveCharts: React.FC<{ detailed?: boolean }> = ({ detailed }) => (
  <Grid cols={2} className='mb-4'>
    <Panel title='Drive Distance vs FED' sub={detailed ? 'Weekly km vs Fatigue Exposure Dose' : undefined}>
      <BucketChart data={FED_BY_KM} domain={[0, 100]} fill='var(--color-series-1)' name='FED' />
    </Panel>
    <Panel
      title={detailed ? 'Drive Days vs Sleep Quality' : 'Drive Days vs Sleep'}
      sub={detailed ? 'Days driving per week vs avg sleep' : undefined}
    >
      <BucketChart data={SLEEP_BY_DAYS} domain={[3, 8.5]} fill='var(--color-info)' name='Avg sleep (h)' />
    </Panel>
  </Grid>
);

export const DrivingScreen: React.FC = () => {
  const d = useDash();
  const { drillRegion } = d;
  const workers = scopedWorkers(d);

  if (d.drillScope === 'company') {
    const allW = wArr();
    const allDrivers = allW.filter((w) => w.driveKm > 0);
    const allNon = allW.filter((w) => w.driveKm === 0);
    return (
      <div>
        <DriveKpis workers={allW} />
        <AiInsight>{genInsight('driving', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
        <Banner tone='info' className='my-4'>
          Drivers average FED {avgFED(allDrivers) - avgFED(allNon)} points higher than non-drivers — driving contributes
          directly to in-work fatigue.
        </Banner>
        <SecHeader>Driving Exposure by Region — click to view detail</SecHeader>
        <Grid cols={2} className='mb-4'>
          {Object.entries(REGIONS).map(([rid, reg]) => {
            const rd = workersInRegion(rid).filter((w) => w.driveKm > 0);
            const rRisk = rd.filter((w) => w.driveKm >= 700).length;
            const rAvgKm = rd.length ? +(rd.reduce((a, w) => a + w.driveKm, 0) / rd.length).toFixed(0) : 0;
            return (
              <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => drillRegion(rid)}
                  onKeyDown={(e) => e.key === 'Enter' && drillRegion(rid)}
                >
                  <div className='mb-3 flex items-center gap-2'>
                    <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{ background: reg.color }} />
                    <span className='font-semibold'>{reg.name}</span>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <StatTile value={rd.length} label='Active drivers' tone='muted' />
                    <StatTile value={rRisk} label='EU risk' tone={rRisk ? 'bad' : 'muted'} alert={rRisk > 0} />
                    <StatTile value={rAvgKm} label='Avg km/wk' />
                    <StatTile value={avgFED(rd)} label='Avg driver FED' tone='info' />
                  </div>
                </div>
              </Panel>
            );
          })}
        </Grid>
        <DriveCharts detailed />
      </div>
    );
  }

  if (d.drillScope === 'region' && d.activeRegion) {
    return (
      <div>
        <RegionPicker />
        <DriveKpis workers={workers} />
        <AiInsight>
          {genInsight('driving', workers, { regionId: d.activeRegion, scopeName: REGIONS[d.activeRegion].name })}
        </AiInsight>
        <div className='mt-4'>
          <DriveCharts />
        </div>
        <DriveTable workers={workers} />
      </div>
    );
  }

  if (d.drillScope === 'group' && d.activeGroup !== null) {
    return (
      <div>
        <RegionPicker resetGroup compact />
        <GroupPicker regionId={GROUPS[d.activeGroup].region} />
        <DriveKpis workers={workers} />
        <AiInsight>{genInsight('driving', workers, { scopeName: GROUPS[d.activeGroup].name })}</AiInsight>
        <div className='mt-4'>
          <DriveTable workers={workers} />
        </div>
      </div>
    );
  }

  const w = d.activeWorker ? WORKERS[d.activeWorker] : undefined;
  return (
    <div>
      <WorkerPicker />
      {w && (
        <>
          <AiInsight>{genInsight('driving', [w], { scopeName: w.name })}</AiInsight>
          <div className='mt-4'>
            <DriveTable workers={[w]} />
          </div>
        </>
      )}
    </div>
  );
};
